'use client';

import { useEffect } from 'react';
import { useSetState } from 'minimal-shared/hooks';
import { useRouter } from 'src/routes/hooks';
import { paths, newSearchParams } from 'src/routes/paths';
import { useTranslate } from 'src/locales';
import { getData, deleteData, updateData, createBatch, serverTimestamp, distributedCounter } from 'src/lib/firebase';
import { useAuthContext, useRedirectToSignIn } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export function useFavoriteProduct({
  id,
  preview,
  defaultValues
}) {
  const router = useRouter();
  const {
    user
  } = useAuthContext();
  const {
    currentLang
  } = useTranslate();
  const urlToSignIn = useRedirectToSignIn();
  const {
    state,
    setState
  } = useSetState({
    loading: false,
    isFavorite: !!defaultValues?.isLiked,
    totalFavorites: defaultValues?.totalLikes ?? 0
  });
  const toggleFavorite = async () => {
    if (!user?.id) {
      router.push(urlToSignIn);
      return;
    }
    if (!user?.profileCompleted) {
      router.push(`${paths.user.account}${newSearchParams({
        country: currentLang.countryName
      })}`);
      return;
    }
    const isFavorite = !state.isFavorite;
    const totalFavorites = state.totalFavorites + (isFavorite ? 1 : -1);
    setState({
      isFavorite,
      totalFavorites,
      loading: !preview
    });
    if (!preview && !state.loading) {
      const currentState = state;
      try {
        const batch = createBatch();
        distributedCounter(`promotions/${id}`, 'totalLikes', isFavorite ? 1 : -1, batch);
        if (isFavorite) {
          updateData(`users/${user.id}/favorites/${id}`, {
            createdAt: serverTimestamp()
          }, batch);
        } else {
          deleteData(`users/${user.id}/favorites/${id}`, batch);
        }
        await batch.commit();
        setState({
          isFavorite,
          loading: false,
          totalFavorites: currentState.totalFavorites + (isFavorite ? 1 : -1)
        });
      } catch (error) {
        console.error(error);
        setState(currentState);
      }
    }
  };
  const fetchFavorite = async () => {
    if (!id || !user?.id || preview) {
      setState({
        isFavorite: false
      });
      return;
    }
    const favoriteProduct = await getData({
      path: `users/${user?.id}/favorites/${id}`
    });
    setState({
      isFavorite: !!favoriteProduct?.id
    });
  };
  useEffect(() => {
    fetchFavorite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  return {
    ...state,
    toggleFavorite
  };
}